import { CommonPublishStatus, EPublishStatus, IRunRecordInfo, TaskStatisticsVo } from '@/constants/geo';
import service from '.';
import { EAnalysisStrategy, EAutoUpdateType } from './spider';

/**
 * PlatformMapping，平台映射关系
 */
export interface PlatformMapping {
  /**
   * 平台名称
   */
  name?: string;
  /**
   * 平台
   */
  platform?: string;
  [property: string]: any;
}

/**
 * 任务运行状态
 */
export enum ETaskRunStatus {
  Failed = 'FAILED',
  Running = 'RUNNING',
  Stopped = 'STOPPED',
  Succ = 'SUCC',
}
/**
 * 任务来源
 */
export enum ETaskSource {
  Company = 'COMPANY',
  Task = 'TASK',
  Analysis = 'ANALYSIS',
}
export interface CompanyVo {
  /**
   * 品牌
   */
  brand: string;
  /**
   * 套餐
   */
  combo: string;
  customerType: string;
  industry: string;
  /**
   * 公司id
   */
  companyId: string;
  /**
   * 公司名称
   */
  companyName: string;
  /**
   * 创建时间
   */
  createTime: Date;
  /**
   * 供应商
   */
  provider: string;
  /**
   * 备注
   */
  remark: string;
  /**
   * 选择更新的报告任务ID列表
   */
  recordIds: string[];
  /**
   * 选择更新的报告平台列表
   */
  platforms: string[];
  /**
   * 企业资料
   */
  information: string;
  /**  * 竞品
   */
  competitors: string[];
  /**
   * 最新曝光
   */
  latestExposure: number;
  latestTaskTime: string;
  latestTaskName: string;
  expireTime?: string | Date;
  spUserId: string;
  apUserId: string;
  providerInfo?: {
    userId?: number | string;
    nickName?: string;
    nickname?: string;
    email?: string;
    status?: string;
  };
  agentInfo?: {
    userId?: number | string;
    nickName?: string;
    nickname?: string;
    email?: string;
    status?: string;
  };
  status?: CompanyStatus;
  [property: string]: any;
}
/**
 * ModifyCompanyCompactWordReq，修改公司词组请求
 */
export interface ModifyCompanyCompactWordReq {
  /**
   * 公司Id
   */
  companyId: string;
  /**
   * 公司词组详情
   */
  wordMap: CompactWord[];
  [property: string]: any;
}

/**
 * CompactWord，组词配置
 */
export interface CompactWord {
  /**
   * 词类型
   */
  type: ECompactWordType;
  /**
   * 词列表
   */
  words: string[];
  [property: string]: any;
}

/**
 * 词类型
 */
export enum ECompactWordType {
  BrandPrefix = 'BRAND_PREFIX',
  Core = 'CORE',
  EntitySuffix = 'ENTITY_SUFFIX',
  LocationPrefix = 'LOCATION_PREFIX',
  QuestionAnswerSuffix = 'QUESTION_ANSWER_SUFFIX',
  QuestionPrefix = 'QUESTION_PREFIX',
  RecommendPrefix = 'RECOMMEND_PREFIX',
  RecommendSuffix = 'RECOMMEND_SUFFIX',
  ScenePrefix = 'SCENE_PREFIX',
}
export type ECompactWordTypeExcludeCore = Exclude<ECompactWordType, ECompactWordType.Core>;
export const compactWordKeyMap: {
  [key in ECompactWordType]: string;
} = {
  [ECompactWordType.QuestionPrefix]: '提问词前缀',
  [ECompactWordType.LocationPrefix]: '区位词前缀',
  [ECompactWordType.RecommendPrefix]: '推荐词前缀',
  [ECompactWordType.BrandPrefix]: '品牌词前缀',
  [ECompactWordType.Core]: '核心词',
  [ECompactWordType.ScenePrefix]: '场景词前缀',
  [ECompactWordType.EntitySuffix]: '实体词后缀',
  [ECompactWordType.RecommendSuffix]: '推荐词后缀',
  [ECompactWordType.QuestionAnswerSuffix]: '问答词后缀',
};

/**
 * CompanyStaticsRes，公司统计响应
 */
export interface CompanyStaticsRes {
  /**
   * 消费发布文章金额
   */
  articleAmount?: number;
  /**
   * 发布文章数量
   */
  articleCount?: number;
  /**
   * 公司ID
   */
  companyId?: number;
  /**
   * 消费总金额
   */
  totalAmount?: number;
  [property: string]: any;
}

export type OrderType = 'ASC' | 'DESC';

export interface OrderItem {
  field:
    | 'companyId'
    | 'companyName'
    | 'latestExposure'
    | 'analysisExposure'
    | 'latestTaskTime'
    | 'totalVisibility'
    | 'doubaoVisibility'
    | 'deepseekVisibility'
    | 'yuanbaoVisibility'
    | 'latestArticlePublishTime';
  type: OrderType;
}

export interface CompanyMonitorPageReq {
  pageIndex?: number;
  pageSize?: number;
  companyName?: string;
  statuses?: CompanyStatus[];
  combos?: string[];
  providers?: string[];
  industries?: string[];
  customerTypes?: string[];
  totalVisibilityMin?: number;
  totalVisibilityMax?: number;
  doubaoVisibilityMin?: number;
  doubaoVisibilityMax?: number;
  deepseekVisibilityMin?: number;
  deepseekVisibilityMax?: number;
  yuanbaoVisibilityMin?: number;
  yuanbaoVisibilityMax?: number;
  orderItems?: OrderItem[];
}

export type CompanyStatus =
  | 'DELIVERING'
  | 'FIRST_WAIT_ACCEPTANCE'
  | 'FIRST_OVERDUE'
  | 'ONLINE'
  | 'FIRST_ONLINE_OPTIMIZING'
  | 'SUPPLEMENT_DELIVERING'
  | 'SUPPLEMENT_WAIT_ACCEPTANCE'
  | 'SUPPLEMENT_NOT_STANDARD'
  | 'SUPPLEMENT_OPTIMIZED'
  | 'TESTING'
  | 'STOPPED';

export interface CompanyMonitorPageRow {
  companyId: string;
  companyName: string;
  totalVisibility: number;
  doubaoVisibility: number;
  deepseekVisibility: number;
  yuanbaoVisibility: number;
  status: CompanyStatus;
  statusDesc:
    | '首次交付 - 交付中'
    | '首次交付 - 待验收'
    | '首次交付 - 已超期'
    | '首次交付 - 已上线'
    | '首次交付 - 已上线+继续优化'
    | '补词优化 - 交付中'
    | '补词优化 - 待验收'
    | '补词优化 - 未达标'
    | '补词优化 - 已优化'
    | '测试'
    | '停止交付';
  latestArticlePublishTime: string | null;
  latestTaskTime?: string | null;
  combo?: string;
  provider?: string;
  industry?: string;
  customerType?: string;
}

export interface CompanyFieldOptions {
  combos: string[];
  providers: string[];
  industries: string[];
  customerTypes: string[];
}
export interface CompanyImageVo {
  imageId: string;
  companyId: string;
  imageName: string;
  originalFileName?: string;
  fileType?: string;
  fileSize?: number;
  imageUrl: string;
  thumbnailUrl: string;
  htmlTag: string;
  createdTime?: string;
  updatedTime?: string;
}

export interface CompanyImageUploadPolicyVo {
  objectKey: string;
  uploadObjectKey: string;
  uploadUrl: string;
  imageUrl: string;
  thumbnailUrl: string;
  accessKeyId: string;
  policy: string;
  signature: string;
  successActionStatus?: string;
  expireTime?: string;
}

const getImageContentType = (file: File) => {
  if (file.type) {
    return file.type;
  }
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'jpg') {
    return 'image/jpeg';
  }
  return ext ? `image/${ext}` : 'application/octet-stream';
};

const uploadImageToOss = (
  policy: CompanyImageUploadPolicyVo,
  file: File,
  onUploadProgress?: (event: any) => void
) =>
  new Promise<void>((resolve, reject) => {
    const formData = new FormData();
    formData.append('key', policy.uploadObjectKey);
    formData.append('OSSAccessKeyId', policy.accessKeyId);
    formData.append('policy', policy.policy);
    formData.append('Signature', policy.signature);
    formData.append('success_action_status', policy.successActionStatus || '200');
    formData.append('Content-Type', getImageContentType(file));
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', policy.uploadUrl);
    xhr.upload.onprogress = (event) => {
      onUploadProgress?.(event);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`OSS upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('OSS upload failed'));
    xhr.send(formData);
  });

export enum CompetitorMode {
  None = 'NONE',
  RandomN = 'RANDOM_N',
}

export enum WordMode {
  All = 'ALL',
  None = 'NONE',
}

export enum EntityWordMode {
  One = 'ONE',
  All = 'ALL',
  None = 'NONE',
}

export interface CreateOptimizedCompanyArticleReq {
  autoPublish: boolean;
  /**
   * 注意：必须使用 string[] 传递，避免长整型 companyId 在链路中发生精度损失。
   */
  companyIds: string[];
  competitorMode: CompetitorMode;
  competitorRandomCount?: number;
  coreWordVisibilityMax: number;
  coreWordVisibilityMin: number;
  entityWordMode: EntityWordMode;
  locationWordMode: WordMode;
  platforms: string[];
  remark?: string;
  sceneWordMode: WordMode;
  combinations: {
      articleCount: number;
      combinationId?: number;
      combinationName?: string;
      items: {
        /**
         * 注意：使用 string 传递，避免 ID 精度问题。
         */
        articleTypeId: string;
        id?: number;
        mediaId: number;
      }[];
    }[];
}

export interface CreateOptimizedCompanyArticleRes {
  successCount: number;
  failedCount: number;
  results: {
    companyId: string;
    success: boolean;
    articleIds?: string[];
    errorMsg?: string | null;
  }[];
}

/**
 * 公司通用配置类型
 */
export enum ECompanyCommonConfigType {
  CompetitorCompare = 'COMPETITOR_COMPARE',
}

export interface CompanyCommonConfigQueryReq {
  companyId: string;
  configType: ECompanyCommonConfigType;
}

export interface CompanyCommonConfigUpdateReq extends CompanyCommonConfigQueryReq {
  values: string[];
}

export const companyApi = {
  list: (data: {
    pageIndex: number;
    pageSize: number;
    companyName?: string;
    analysisExposureMin?: number;
    analysisExposureMax?: number;
    expireTimeStart?: string;
    expireTimeEnd?: string;
    statuses?: CompanyStatus[];
    combos?: string[];
    providers?: string[];
    industries?: string[];
    customerTypes?: string[];
    orderItems?: OrderItem[];
  }) => service.post<List<CompanyVo>>('/company/page', data),
  dashboardPage: (data: CompanyMonitorPageReq) =>
    service.post<List<CompanyMonitorPageRow>>('/company/dashboard/page', data),
  fieldOptions: () => service.get<CompanyFieldOptions>('/company/field-options'),
  myList: () => service.get<{ companyId: string; companyName: string }[]>('/company/list-simple'),
  get: (companyId: string) => service.get<CompanyVo>(`/company/detail`, { params: { companyId } }),
  create: (data: Partial<CompanyVo>) => service.post('/company/create', data),
  update: (data: Partial<CompanyVo>) => service.post('/company/update', data),
  /** 更新企业资料 */
  updateInformation: (data: { companyId: string; information: string }) =>
    service.post(`/company/update-information`, data),
  delete: (companyId: string) => service.post(`/company/delete`, { companyId }),
  /** 获取公司组词详情 */
  getWordInfo: (companyId: string) =>
    service.get<{
      componentWords: {
        type: ECompactWordType;
        words: string[];
        description?: string;
      }[];
      companyId: string;
      coreWords: {
        type: ECompactWordType;
        words: string[];
        description?: string;
      };
    }>(`/company/word/word-list`, { params: { companyId } }),
  getCommonConfigList: async (data: CompanyCommonConfigQueryReq) => {
    const { companyId, configType } = data;
    switch (configType) {
      case ECompanyCommonConfigType.CompetitorCompare: {
        const res = await service.get<{ competitors?: string[] }>(`/company/competitor/compare-config`, {
          params: { companyId },
        });
        return {
          configType,
          values: res?.competitors || [],
        };
      }
      default:
        return {
          configType,
          values: [],
        };
    }
  },
  updateCommonConfigList: async (data: CompanyCommonConfigUpdateReq) => {
    const { companyId, configType, values } = data;
    switch (configType) {
      case ECompanyCommonConfigType.CompetitorCompare:
        return service.post(`/company/competitor/compare-config/update`, {
          companyId,
          competitors: values,
        });
      default:
        return Promise.resolve();
    }
  },
  getCompetitorCompareConfig: (companyId: string) =>
    service.get<{ competitors?: string[] }>(`/company/competitor/compare-config`, { params: { companyId } }),
  updateCompetitorCompareConfig: (data: { companyId: string; competitors: string[] }) =>
    service.post(`/company/competitor/compare-config/update`, data),
  updateWordInfo: (data: ModifyCompanyCompactWordReq) => service.post(`/company/word/modify`, data),
  /** 更新公司报告 */
  updateReport: (data: { companyId: string; taskIds: string[] }) => service.post(`/company/update-report`, data),
  /** 创建分析报告 */
  createAnalysis: (data: {
    companyId: string;
    prePerformanceTaskId: string;
    postPerformanceTaskId: string;
    reportCycle?: 'UNSET' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  }) =>
    service.post(`/company/analysis/create`, data),
  // 更新企业看板上的分析报告
  updateReportSetting: (data: { companyId: string; reportId: string | null }) =>
    service.post(`/company/report/setting`, data),
  // 获取企业更新到看板的分析报告
  getAnalysisReport: (params: { companyId: string }) =>
    service.get<{
      title: string;
      reportId: string;
      createTime: string;
    }>(`/company/get-analysis-report`, { params }),
  getStaticsBase: (companyId: string) =>
    service.get<CompanyStaticsRes>(`/company/statics/base`, { params: { companyId } }),
  listImages: (companyId: string) =>
    service.get<CompanyImageVo[]>(`/company/image/list`, { params: { companyId } }),
  uploadImage: async (data: { companyId: string; file: File; onUploadProgress?: (event: any) => void }) => {
    const policy = await service.post<CompanyImageUploadPolicyVo>(`/company/image/upload-policy`, {
      companyId: data.companyId,
      originalFileName: data.file.name,
      fileSize: data.file.size,
    });
    await uploadImageToOss(policy, data.file, data.onUploadProgress);
    return service.post<CompanyImageVo>(`/company/image/complete`, {
      companyId: data.companyId,
      objectKey: policy.objectKey,
      originalFileName: data.file.name,
      imageName: data.file.name.replace(/\.[^.]+$/, ''),
      fileSize: data.file.size,
    });
  },
  updateImage: (data: { companyId: string; imageId: string; imageName: string }) =>
    service.post(`/company/image/update`, data),
  deleteImage: (data: { companyId: string; imageId: string }) => service.post(`/company/image/delete`, data),
  createOptimizedArticle: (data: CreateOptimizedCompanyArticleReq) =>
    service.post<CreateOptimizedCompanyArticleRes>('/company/article/create-optimized', data),
};
/**
 * RunGeoTaskReq，运行任务请求
 */
export interface RunGeoTaskReq {
  /**
   * 是否自动分析
   */
  analysisStrategy?: EAnalysisStrategy;
  /**
   * 是否添加到监控
   */
  autoAddMonitor?: boolean;
  /**
   * 是否自动更新看板
   */
  autoUpdateBoard?: EAutoUpdateType;
  /**
   * 是否发布到客户后台
   */
  publishToClient?: EPublishStatus;

  /**
   * 词组ID
   */
  componentWords?: number[];
  /**
   * 扩展规则ID
   */
  expandWords?: number[];
  /**
   * 任务ID/公司Id
   */
  id: string;
  /**
   * 运行平台
   */
  platforms?: string[];
  /**
   * 任务来源
   */
  source?: ETaskSource;
  /**  * 任务名称
   */
  taskName?: string;
}
export interface TaskVo {
  /**
   * 任务创建时间
   */
  createTime?: Date;
  /**
   * 任务结束时间
   */
  finishTime?: Date;
  /**
   * 任务运行记录ID
   */
  id: number;
  /**
   * 运行的平台
   */
  platformMappings?: PlatformMapping[];
  /**
   * 任务运行状态
   */
  runStatus?: ETaskRunStatus;
  /**
   * 任务开始时间
   */
  startTime?: Date;
  /**
   * 任务ID
   */
  taskId?: number;
  taskName?: string;
}
export enum ERerunStrategy {
  Fail = 'FAIL',
  All = 'ALL',
  Invisible = 'INVISIBLE',
}

/**
 * 关键词类型
 */
export enum EKeywordType {
  Brand = 'BRAND',
  Expand = 'EXPAND',
  Search = 'SEARCH',
}
export interface TaskSummaryVo {
  // 可见度
  aiVisibility: {
    visiblePercent: number;
  };
  // 排名
  rankingStatistic: {
    averageRank: number;
  };
  // 竞品份额
  competitorItemResList: { name: string; count: number; percent: number }[];
  // 引用信源
  referenceSourceStatistics: {
    sourceName: string;
    count: number;
  }[];
  // 舆情分布
  aiInclination: {
    positiveTotal: number;
    neutralTotal: number;
    negativeTotal: number;
    positivePercent: number;
    neutralPercent: number;
    negativePercent: number;
  };
  // 信息准确性
  accuracy: number;
}

export const taskApi = {
  list: (data: { pageIndex: number; pageSize: number; taskId?: string; source: ETaskSource; search?: string }) =>
    service.post<List<IRunRecordInfo>>('/geo/task/run-record/page', data),
  listOptions: (data: {
    pageIndex: number;
    pageSize: number;
    taskName?: string;
    companyId?: string;
    statuses?: string[];
  }) => service.post<List<TaskVo>>('/geo/task/run-record/options', data),
  create: (data: Partial<RunGeoTaskReq>) => service.post('/geo/task/run/v2', data),
  publish: (recordIds: Array<string | number>) => service.post('/geo/task/run-record/publish', { recordIds }),
  unpublish: (recordIds: Array<string | number>) => service.post('/geo/task/run-record/unpublish', { recordIds }),
  batchPublish: (recordIds: Array<string | number>) => service.post('/geo/task/run-record/publish', { recordIds }),

  delete: (id: string) => service.delete(`/geo/task/${id}`),
  updateTaskExpand: (data: { taskId: string; expandWords: string[] }) =>
    service.post(`/geo/task/run-record/update/task-expand`, data),
  getTaskDetail: (id: string) =>
    service.get<TaskStatisticsVo>(`/geo/task/run-record/task-detail`, { params: { taskId: id } }),
  rerun: (data: {
    /**
     * 平台
     */
    platforms: string[];
    /**
     * 重试策略
     */
    strategy: ERerunStrategy;
    /**
     * 任务id
     */
    taskId: string;
  }) => service.post(`/geo/task/retry`, data),
  download: (data: {
    /**
     * 平台类型
     */
    platform?: string[];
    /**
     * 运行记录ID列表
     */
    recordIds?: string[];
    /**
     * 推荐性
     */
    sentiment?: string[];
    /**
     * 任务运行状态
     */
    status?: string;
    /**
     * 是否可见
     */
    visibility?: boolean;
    [property: string]: any;
  }) => service.post(`/geo/task/run-record/download-detail`, data),
  // 任务分析结果分页
  pageAnalysisResult: (data: {
    /**
     * 任务Id/公司Id
     */
    id: string;
    /**
     * 关键词类型
     */
    type?: EKeywordType;
    /**
     * 当前页码
     */
    pageIndex?: number;
    /**
     * 每页数量
     */
    pageSize?: number;
    /**
     * 平台
     */
    platform?: string;
    /**
     * 报表数据来源
     */
    source?: ETaskSource;
    [property: string]: any;
  }) => service.post(`/geo/task/run-record/page-analysis-result`, data),
  getSummary: (taskId: string) =>
    service.get<TaskSummaryVo>(`/geo/task/run-record/task-detail2`, { params: { taskId } }),
};

/**
 * PhraseItemRes
 */
export interface PhraseItemRes {
  categories?: WordCategory[];
  coreWords?: string[];
  name?: string;
  recordId: string;
  wordCount?: number;
  expandCount?: number;
  [property: string]: any;
  publishStatus?: CommonPublishStatus;
  publishStatusText?: string;

}

/**
 * PhraseDetailVo，词条详情
 */
export interface PhraseDetailVo {
  /**
   * 公司ID
   */
  companyId?: number;
  /**
   * 组词规则
   */
  componentWords?: { [key: string]: string[] };
  /**
   * 核心词
   */
  coreWords?: string[];
  /**
   * 创建时间
   */
  createTime?: Date;
  /**
   * 扩展词个数
   */
  expandWordCount?: number;
  /**
   * 扩展词规则
   */
  expandWordRule?: { [key: string]: string[] }[];
  /**
   * 主键ID
   */
  id?: number;
  /**
   * 词组名称
   */
  name?: string;
  /**
   * 更新时间
   */
  updateTime?: Date;
  /**
   * 词组个数
   */
  wordCount?: number;
  [property: string]: any;
}
/**
 * WordCategory
 */
export interface WordCategory {
  description?: string;
  type?: ECompactWordType;
  words?: string[];
  [property: string]: any;
}
/**
 * EntityWord
 */
export interface EntityWord {
  /**
   * 词条ID
   */
  id: string;
  /**
   * 词条内容
   */
  word: string;
  [property: string]: any;
}
export const phraseApi = {
  list: (data: { pageIndex: number; pageSize: number; companyId: string; type: 'SEARCH_WORD' | 'EXPAND_WORD' }) =>
    service.post<List<PhraseItemRes>>('/company/word/record/page', data),
  getEntities: (data: { pageIndex: number; pageSize: number; recordId: string }) =>
    service.post<List<EntityWord>>(`/company/word/entity/page`, data),
  getDetail: (recordId: string) => service.get<PhraseDetailVo>(`/company/word/record/detail`, { params: { recordId } }),
  update: (data: {
    /**
     * 追加词条
     */
    appendWords?: string[];
    /**
     * 删除词条ID
     */
    deleteIds?: string[];
    /**
     * 修改词条
     */
    modifyWords?: EntityWord[];
    recordId: string;
    name?: string;
    // type?: string;
  }) => service.post(`/company/word/entity/modify`, data),
  create: (data: {
    /**
     * 公司Id
     */
    companyId: string;
    /**
     * 公司词组详情
     */
    wordMap: CompactWord[];
    /**
     * 扩展词规则
     */
    expandWords?: CompactWord[][];
    //是否发布用户后台
    publishToClient?: EPublishStatus;

    /**
     * 词组名称
     */
    name: string;
    words: string[];
    type: string;
  }) => service.post('/company/word/record/create', data),
  createExpand: (data: {
    /**
     * 公司Id
     */
    companyId: string;
    wordMap: CompactWord[];
    /**
     * 规则名称
     */
    name: string;
  }) => service.post('/company/word/record/create-expand', data),
  updateExpand: (data: { recordId: string; name?: string; wordMap: CompactWord[] }) =>
    service.post('/company/word/entity/modify-expand', data),
  delete: (recordIds: string[]) => service.post(`/company/word/record/delete`, { recordIds }),
  calc: (data: { wordMap: CompactWord[]; expandWords?: CompactWord[][] }) =>
    service.post<{ expandAmount: number; total: number }>(`/company/word/record/calc`, data),
  publish: (recordId: string) => service.post('/company/word/record/publish', { recordId }),
  unpublish: (recordId: string) => service.post('/company/word/record/unpublish', { recordId }),
};

// 企业文章

/**
 * 文章状态
 */
export enum ArticleStatus {
  GENERATE_FAIL = 'GENERATE_FAIL',
  WAITING = 'WAITING',
  RUNNING = 'RUNNING',
  WAIT_PUBLISH = 'WAIT_PUBLISH',
  PUBLISH_ING = 'PUBLISH_ING',
  RELEASE = 'RELEASE',
  RELEASED = 'RELEASED',
  RELEASE_FAIL = 'RELEASE_FAIL',
}
export const articleStatusMap: Record<ArticleStatus, string> = {
  [ArticleStatus.GENERATE_FAIL]: '生成失败',
  [ArticleStatus.WAITING]: '待生成',
  [ArticleStatus.RUNNING]: '生成中',
  [ArticleStatus.WAIT_PUBLISH]: '待发布',
  [ArticleStatus.PUBLISH_ING]: '发布中',
  [ArticleStatus.RELEASE]: '发布待返链',
  [ArticleStatus.RELEASED]: '发布成功',
  [ArticleStatus.RELEASE_FAIL]: '发布失败',
};

export type ArticlePublishStatus = EPublishStatus;

/**
 * ICompanyArticle，公司文章分页
 */
export interface ICompanyArticle {
  /**
   * 文章id
   */
  articleId: string;
  /**
   * 文章自动发布
   */
  // autoPublish: AutoPublish;
  /**
   * 文章分类名称
   */
  categoryName: string;
  /**
   * 公司id
   */
  companyId: number;
  /**
   * 公司名称
   */
  companyName: string;
  /**
   * 文章使用竞品公司名称
   */
  competitors: string[];
  /**
   * 创建时间
   */
  createTime: Date;
  /**
   * 文章生成时间
   */
  generateTime?: Date;
  /**
   * 文章发布媒体Id
   */
  mediaId?: number;
  /**
   * 文章发布媒体名称
   */
  mediaName?: string;
  /**
   * 平台名称
   */
  platformName?: string;
  /**
   * 文章价格
   */
  price?: number;
  /**
   * 文章发布时间
   */
  publishTime?: Date;
  /**
   * 客户后台发布状态
   */
  publishStatus?: CommonPublishStatus;
  /**
   * 发布链接
   */
  publishUrl?: string;
  /**
   * 被引用次数
   */
  referenceCount?: number;
  quoteCount?: number;
  quotedCount?: number;
  citedCount?: number;
  /**
   * 失败原因
   */
  reason?: string;
  /**
   * 备注
   */
  remark?: string;
  /**
   * 文章状态
   */
  status: ArticleStatus;
  /**
   * 文章使用模板Id
   */
  templateId?: number;
  /**
   * 文章使用模板名称
   */
  templateName?: string;
  /**
   * 第三方订单ID
   */
  thirdPartyOrderId?: string;
  /**
   * 文章标题
   */
  title: string;
  [property: string]: any;
}

export interface CompanyArticleImportResult {
  total: number;
  imported: number;
  skipped: number;
  totalPrice?: number;
  messages?: {
    rowNum?: number;
    message?: string;
  }[];
}

export interface CompanyArticleMediaOption {
  mediaId?: number;
  mediaName: string;
  channelType?: string;
}
// 竞品信息
export interface CompetitorVo {
  competitors: {
    competitorName: string;
    id: string;
    introduce: string;
  }[];
}
/**
 * ModifyCompanyCompetitorReq，修改公司竞品参数
 */
export interface ModifyCompanyCompetitorReq {
  /**
   * 新增的
   */
  add?: CreateCompetitorItemReq[];
  /**
   * 公司id
   */
  companyId: string;
  /**
   * 删除的Id
   */
  deleteIds?: string[];
  /**
   * 修改的
   */
  modify?: ModifyCompetitorItemReq[];
  [property: string]: any;
}

/**
 * CreateCompetitorItemReq，创建竞品项
 */
export interface CreateCompetitorItemReq {
  /**
   * 竞品名称
   */
  competitorName?: string;
  /**
   * 竞品介绍
   */
  introduce?: string;
  [property: string]: any;
}

/**
 * ModifyCompetitorItemReq，修改竞品信息
 */
export interface ModifyCompetitorItemReq {
  /**
   * 竞品名称
   */
  competitorName?: string;
  /**
   * 竞品id
   */
  id?: number;
  /**
   * 竞品介绍
   */
  introduce?: string;
  [property: string]: any;
}

export const articleApi = {
  list: (data: {
    pageIndex: number;
    pageSize: number;
    companyId?: string;
    status?: ArticleStatus[];
    publishStatus?: EPublishStatus[];
    mediaIds?: number[];
    mediaNames?: string[];
    priceMin?: number;
    priceMax?: number;
    search?: string;
    referenceCountMin?: number;
    referenceCountMax?: number;
    generateTimeStart?: string;
    generateTimeEnd?: string;
  }) =>
    service.post<List<ICompanyArticle>>(`/company/article/page`, data),
  listPublishedMediaOptions: () => service.get<CompanyArticleMediaOption[]>(`/company/article/published-media-options`),
  get: (articleId: string) => service.get<ICompanyArticle>(`/company/article/detail`, { params: { articleId } }),
  update: (data: {
    articleId: string;
    title?: string;
    publishUrl?: string;
    content: string;
    mediaId?: string;
    status?: ArticleStatus;
  }) =>
    service.post(`/company/article/update`, data),
  create: (data: {
    companyId: string;
    competitorIds: string[];
    coreWords: string[];
    locationWords?: string[];
    sceneWords?: string[];
    entityWords?: string[];
    imageIds?: string[];
    categories: { category: string; count: string[] }[];
  }) => service.post('/company/article/create', data),
  delete: (articleId: string[]) => service.post(`/company/article/delete`, { articleId }),
  retry: (articleId: string) => service.post(`/company/article/retry`, { articleId }),
  download: (articleIds: string[]) => service.post(`/company/article/download`, { articleIds }),
  downloadUploadTemplate: () =>
    service.get<Blob>(`/company/article/upload/template`, { responseType: 'blob' }),
  validateUploadArticle: (data: { companyId: string; file: File }) => {
    const formData = new FormData();
    formData.append('companyId', data.companyId);
    formData.append('file', data.file);
    return service.post<CompanyArticleImportResult>(`/company/article/upload/validate`, formData, { isSilence: true });
  },
  uploadArticle: (data: { companyId: string; file: File }) => {
    const formData = new FormData();
    formData.append('companyId', data.companyId);
    formData.append('file', data.file);
    return service.post<CompanyArticleImportResult>(`/company/article/upload`, formData);
  },
  // 竞品
  listCompetitors: (companyId: string) =>
    service.get<
      {
        competitorName: string;
        id: string;
        introduce: string;
      }[]
    >(`/company/competitor/list`, { params: { companyId } }),
  // 更新竞品
  updateCompetitor: (data: ModifyCompanyCompetitorReq) => service.post('/company/competitor/modify', data),
  // 生成竞品
  createCompetitor: (data: {
    companyId: string;
    coreWords: string[];
    count: number;
    region?: string;
    scope?: string;
  }) => service.post('/company/competitor/generate', data),
  // 企业资料
  getCompanyInfo: (companyId: string) =>
    service.get<{ content: string }>(`/company/info/detail`, { params: { companyId } }),
  updateCompanyInfo: (data: { companyId: string; content: string }) => service.post(`/company/info/modify`, data),
  // 发布文章
  publish: (articleIds: string[]) => service.post(`/company/article/publish`, { articleIds }),
  // 取消发布
  unPublish: (articleIds: string[]) => service.post(`/company/article/unpublish`, { articleIds }),
  // 发布到客户后台
  publishToClient: (articleIds: string[]) => service.post(`/company/article/publish-to-client`, { articleIds }),
  // 取消发布到客户后台
  unpublishToClient: (articleIds: string[]) => service.post(`/company/article/unpublish-to-client`, { articleIds }),
};

/**
 * PlatformSummary
 */
export interface PlatformSummary {
  /**
   * 核心词
   */
  coreWord: string;
  /**
   * 不可见数
   */
  invisible: number;
  /**
   * 平台
   */
  platform: string;
  /**
   * 提问词总数
   */
  total: number;
  /**
   * 可见数
   */
  visibility: number;
  [property: string]: any;
}

/**
 * CompanyDataDashboardRes，公司数据看板
 */
export interface CompanyDataDashboardRes {
  /**
   * 公司id
   */
  companyId?: number;
  /**
   * 不可见数
   */
  invisible: number;
  /**
   * 数据详情
   */
  result?: DataDashboardResult[];
  /**
   * 任务id
   */
  taskId?: number;
  /**
   * 任务时间
   */
  taskTime?: string;
  /**
   * 提问词总数
   */
  total: number;
  /**
   * 更新时间
   */
  updateTime?: string;
  /**
   * 可见数
   */
  visibility: number;
  [property: string]: any;
}

/**
 * DataDashboardResult，数据详情
 */
export interface DataDashboardResult {
  /**
   * 平台数据
   */
  children: PlatformSummary[];
  /**
   * 核心词
   */
  coreWord: string;
  /**
   * 不可见数
   */
  invisible: number;
  /**
   * 平台
   */
  platform?: string;
  /**
   * 提问词总数
   */
  total: number;
  /**
   * 可见数
   */
  visibility: number;
  [property: string]: any;
}
/** CompanyDashboardResultVo，公司数据看板详情
 */
export interface ICompanyDashboardResultVo {
  visibilities: {
    wordId: string;
    word: string;
    coreWord: string;
    platform: string;
  }[];
  invisibles: {
    wordId: string;
    word: string;
    coreWord: string;
    platform: string;
  }[];
}
export const companyDashboardApi = {
  getSummary: (companyId: string) =>
    service.get<CompanyDataDashboardRes>(`/company/dashboard/detail`, { params: { companyId } }),
  getDetail: (data: { taskId: string; coreWord?: string; platform?: string }) =>
    service.post<ICompanyDashboardResultVo>(`/geo/task/run-record/word-detail`, data),
};
